// Global imports
window.$ = window.jQuery = require('jquery') // Necessary for client/lib/request
// Vendor imports
const ReactDOM = require('react-dom')
// Client V1 imports
const airbrake = require('client/lib/airbrake')
// Client V2 imports
const { applyReportedArticlesCache } = require('client-v2/common/models/article/actions')
const { configureBrowserNavigation } = require('client-v2/common/state/router/events')

// Polyfill object-fit CSS for older browsers
// https://github.com/bfred-it/object-fit-images
require('object-fit-images')()

// Create a namespace for app-related references
const app = window.app = {}

exports.render = function (configureStore, rootFactory) {
  // Setup Redux store from initial state
  const deserializedState = preloadedState()
  const store = app.store = configureStore({
    state: deserializedState
  })

  // Investigating TypeError: Cannot read property 'buildNumber' of null
  // https://app.asana.com/0/102902769366319/682087171396667/f
  const reifiedState = store.getState()
  if (deserializedState && deserializedState.env && !reifiedState.env) {
    airbrake.notify({
      error: new Error('Redux preloaded state reified as empty'),
      params: {
        deserializedState,
        documentBody: document.body.innerHTML,
        reifiedState: typeof reifiedState.toJS === 'function'
          ? reifiedState.toJS()
          : reifiedState
      }
    })
  }

  // Redirect proxied/archived pages to the real server.
  ensureCanonicalDomain(store)

  // Setup libraries that depend on env/user state
  configureLibraries(store)

  // Setup browser to reload for back/forward navigation to this page
  configureBrowserNavigation(store)

  // Prepare the root DOM element and root React component
  const rootElement = document.getElementById('root')
  const rootComponent = rootFactory({ store })

  // Adopt the server-side rendered React Components (if available)
  if (rootElement.hasChildNodes()) {
    ReactDOM.hydrate(rootComponent, rootElement)
  } else {
    ReactDOM.render(rootComponent, rootElement)
  }

  // Reject reported URLs that have been locally cached
  // This has to come after hydration to prevent mismatching
  store.dispatch(applyReportedArticlesCache())
}

function preloadedState() {
  const stateElement = document.getElementById('preloaded-state')
  if (stateElement) {
    const serializedState = stateElement.textContent
    try {
      const deserializedState = JSON.parse(serializedState)
      // Investigating TypeError: Cannot read property 'buildNumber' of null
      // https://app.asana.com/0/102902769366319/682087171396667/f
      if (!deserializedState || !deserializedState.env) {
        airbrake.notify({
          error: new Error('Redux preloaded state deserialized as empty'),
          params: {
            deserializedState,
            serializedState,
            documentBody: document.body.innerHTML
          }
        })
      }
      return deserializedState
    } catch (err) {
      // Investigating TypeError: Cannot read property 'buildNumber' of null
      // https://app.asana.com/0/102902769366319/682087171396667/f
      airbrake.notify({
        error: err,
        params: {
          serializedState
        }
      })
    }
  } else {
    // Investigating TypeError: Cannot read property 'buildNumber' of null
    // https://app.asana.com/0/102902769366319/682087171396667/f
    airbrake.notify({
      error: new Error('Redux preloaded state is undefined'),
      params: {
        documentBody: document.body.innerHTML
      }
    })
  }
}

function ensureCanonicalDomain(store) {
  const { env: { baseUrl } } = store.getState()
  const { href, pathname, search } = window.location
  if (!href.startsWith(baseUrl)) {
    const canonicalHref = baseUrl + pathname + search
    window.location.assign(canonicalHref)
  }
}

function configureLibraries(store) {
  configureAirbrake(store)
  configureSession(store)
}

function configureAirbrake(store) {
  const { env } = store.getState()
  airbrake.configure()
  airbrake.addFilter(notice => {
    // Attach Redux store state to notices
    let state = store.getState()
    if (typeof state.toJS === 'function') {
      // Convert Record to JSON
      state = state.toJS()
    }
    notice.session = state
    return notice
  })
  airbrake.attachBuildNumber(env.buildNumber)
}

function configureSession(store) {
  // Client V1 imports
  const Analytics = require('client/lib/analytics')
  const Request = require('client/lib/request')
  const Session = require('client/lib/sess')

  // Setup user session ID and V1 library helper functions
  const state = store.getState()
  const { buildNumber, csrfToken } = state.env
  // Convert UserRecord to a plain Object for compatibility with wiggin
  const user = state.user.toJS()
  const session = app.session = new Session(user)
  const getSessionId = () => session.update()
  const getPageName = pageContext => {
    if (pageContext && pageContext['page_name']) {
      return pageContext['page_name']
    }
    return store.getState().router.action
  }
  const onCsrfError = () => window.location.reload()

  // Configure V1 libraries to work without wiggin
  Request.configure({
    buildNumber,
    csrfToken,
    getPageName,
    getSessionId,
    onCsrfError
  })
  Analytics.configure({
    buildNumber,
    getPageName,
    getSessionId,
    user
  })
  Analytics.segmentIdentify(user)
  Analytics.branchIdentify(user)

  // Ensure that the initial session ID cookie is set
  // This needs to happen after configuring Request and Analytics
  session.update()
  return session
}
